import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const REASONS = [
  {
    reason: 'Lost Title',
    icon: 'ri-search-line',
    desc: 'Title has been misplaced or cannot be located.',
    docs: ['Valid government-issued photo ID', 'Current vehicle registration', 'Proof of insurance', 'Completed duplicate title application'],
    badge: 'Standard',
    badgeColor: 'bg-[#003366] text-white',
  },
  {
    reason: 'Stolen Title',
    icon: 'ri-shield-cross-line',
    desc: 'Title was stolen and needs immediate replacement.',
    docs: ['Valid photo ID', 'Police report number', 'Current registration', 'Affidavit of loss'],
    badge: 'Priority',
    badgeColor: 'bg-red-600 text-white',
  },
  {
    reason: 'Damaged Title',
    icon: 'ri-file-damage-line',
    desc: 'Title is damaged, torn, or unreadable.',
    docs: ['Valid photo ID', 'Damaged original title (bring it in)', 'Current registration', 'Duplicate title application'],
    badge: 'Same Day',
    badgeColor: 'bg-[#F5A623] text-white',
  },
  {
    reason: 'Lien Release',
    icon: 'ri-bank-line',
    desc: 'Need a clean title after paying off your vehicle loan.',
    docs: ['Valid photo ID', 'Lien release letter from lender', 'Current registration', 'Original title (if available)'],
    badge: 'Express',
    badgeColor: 'bg-green-600 text-white',
  },
];

const SPECIAL_SITUATIONS = [
  { icon: 'ri-bank-line', title: 'Lien on Title', desc: 'We coordinate with lenders for proper documentation and lien release processing.' },
  { icon: 'ri-map-pin-line', title: 'Out-of-State Title', desc: 'Handle interstate title transfer requirements for vehicles from any state.' },
  { icon: 'ri-team-line', title: 'Multiple Owners', desc: 'Ensure all registered owners sign required documents correctly.' },
  { icon: 'ri-heart-line', title: 'Estate Vehicle', desc: 'Process inheritance and probate documentation for vehicles from deceased owners.' },
  { icon: 'ri-building-line', title: 'Business Vehicle', desc: 'Handle corporate documentation requirements for business-registered vehicles.' },
  { icon: 'ri-tools-line', title: 'Salvage Title', desc: 'Process special title designations for salvage or rebuilt vehicles correctly.' },
];

const STEPS = [
  { step: '01', icon: 'ri-user-line', title: 'Verify Identity', desc: 'Confirm ownership and identity with valid photo ID and proof of ownership.' },
  { step: '02', icon: 'ri-file-text-line', title: 'Complete Application', desc: 'Fill out the duplicate title application with vehicle information and reason.' },
  { step: '03', icon: 'ri-money-dollar-circle-line', title: 'Process Payment', desc: 'Pay required fees. We accept all major payment methods.' },
  { step: '04', icon: 'ri-award-line', title: 'Receive New Title', desc: 'New title issued with official seal. Mailed to registered owner address.' },
];

export default function DuplicateTitlePage() {
  const [openReason, setOpenReason] = useState<number | null>(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Duplicate Title Nevada Las Vegas - Lost, Stolen & Damaged Title Replacement',
    description: 'Replace lost, stolen, or damaged Nevada vehicle titles fast in Las Vegas. Same-day duplicate title service available. Licensed Nevada DMV provider. Call 702.979.7202 at Speedy DMV Services.',
    keywords: 'duplicate title Nevada, lost title Las Vegas, replace vehicle title Nevada, duplicate car title Las Vegas, stolen title replacement Nevada',
    canonical: `${siteUrl}/duplicate-title`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema('Duplicate Title Services', 'Fast replacement for lost, stolen, or damaged Nevada vehicle titles in Las Vegas.'),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }, { name: 'Duplicate Title', url: '/duplicate-title' }])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[400px] md:h-[460px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/27be0389019d7472d9e6e4ed18969d42.jpg"
            alt="Duplicate Title Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">Duplicate Title</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl">
              Duplicate Title <span className="text-[#F5A623]">Nevada</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl mb-7">
              Fast replacement for lost, stolen, or damaged vehicle titles. Same-day service available — we handle all the paperwork.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-xs"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '$120–$150 + DMV', label: 'Service Fee' },
              { value: 'Same Day', label: 'In-Office Processing' },
              { value: '2–4 Weeks', label: 'Title Delivery' },
              { value: 'All Cases', label: 'Situations Handled' },
            ].map((s, i) => (
              <div key={i} className="py-4 px-6 text-center">
                <p className="text-[#F5A623] text-lg font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Duplicate Title Process</h2>
          <p className="text-gray-500 mb-10 pl-5">Four simple steps — we handle the complexity so you don't have to.</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/15"></div>
            {STEPS.map((item, i) => (
              <div key={i} className="text-center relative group">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REASONS ACCORDION */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Reasons for Duplicate Title</h2>
          <p className="text-gray-500 mb-8 pl-5">Select your situation to see required documents and next steps.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {REASONS.map((t, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openReason === i ? 'border-[#003366] shadow-md' : 'border-gray-200 hover:border-[#003366]/40'}`}
                onClick={() => setOpenReason(openReason === i ? null : i)}
              >
                <div className={`flex items-center justify-between px-5 py-4 ${openReason === i ? 'bg-[#003366]' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded flex-shrink-0 ${openReason === i ? 'bg-[#F5A623]' : 'bg-[#003366]'}`}>
                      <i className={`${t.icon} ${openReason === i ? 'text-white' : 'text-[#F5A623]'} text-base`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className={`font-bold text-sm ${openReason === i ? 'text-white' : 'text-[#003366]'}`}>{t.reason}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.badgeColor}`}>{t.badge}</span>
                      </div>
                      <p className={`text-xs ${openReason === i ? 'text-white/70' : 'text-gray-400'}`}>{t.desc}</p>
                    </div>
                  </div>
                  <i className={`${openReason === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-xl flex-shrink-0`}></i>
                </div>
                {openReason === i && (
                  <div className="px-5 py-4 bg-white border-t border-gray-100">
                    <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Required Documents</p>
                    <ul className="space-y-2">
                      {t.docs.map((doc, j) => (
                        <li key={j} className="flex items-center space-x-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                        <i className="fas fa-phone text-xs"></i>
                        <span>Call to Confirm Documents</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIAL SITUATIONS */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Special Situations We Handle</h2>
          <p className="text-gray-500 mb-10 pl-5">Complex title situations? We've handled them all.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SPECIAL_SITUATIONS.map((s, i) => (
              <div key={i} className="group flex items-start space-x-4 p-5 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded flex-shrink-0 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${s.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#003366] text-sm mb-1">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEES & TIMELINE */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#003366] px-6 py-4">
                <h3 className="text-white font-bold text-base flex items-center space-x-2">
                  <i className="ri-money-dollar-circle-line text-[#F5A623]"></i>
                  <span>Fee Breakdown</span>
                </h3>
              </div>
              <div className="p-6 space-y-3">
                {[
                  { label: 'Our Service Fee', value: 'Call for Quote' },
                  { label: 'Nevada DMV Duplicate Title Fee', value: '$29.25' },
                  { label: 'Express Processing (if needed)', value: 'Additional fee' },
                  { label: 'Lien Recording (if applicable)', value: 'Varies' },
                ].map((row, i) => (
                  <div key={i} className={`flex justify-between items-center py-2.5 ${i < 3 ? 'border-b border-gray-100' : ''}`}>
                    <span className="text-gray-600 text-sm">{row.label}</span>
                    <span className="font-bold text-[#003366] text-sm">{row.value}</span>
                  </div>
                ))}
                <div className="mt-4 bg-[#FFF8E7] border border-[#F5A623]/30 rounded p-3">
                  <p className="text-xs text-[#7a5c00]"><strong>Note:</strong> Exact fees depend on your specific situation. Call us for a precise quote.</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#003366] px-6 py-4">
                <h3 className="text-white font-bold text-base flex items-center space-x-2">
                  <i className="ri-time-line text-[#F5A623]"></i>
                  <span>Processing Timeline</span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {[
                  { phase: 'In-Office Processing', time: 'Same Day', icon: 'ri-store-2-line', note: 'Document review, fee payment, DMV submission' },
                  { phase: 'DMV Processing', time: '2–4 Weeks', icon: 'ri-government-line', note: 'Nevada DMV processes and issues new title' },
                  { phase: 'Title Delivery', time: 'Mailed to You', icon: 'ri-mail-send-line', note: 'New title mailed to registered owner address' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                      <i className={`${item.icon} text-[#F5A623] text-sm`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="font-bold text-[#003366] text-sm">{item.phase}</p>
                        <span className="text-[#F5A623] text-xs font-bold bg-[#FFF8E7] px-2 py-0.5 rounded">{item.time}</span>
                      </div>
                      <p className="text-gray-400 text-xs">{item.note}</p>
                    </div>
                  </div>
                ))}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white py-3 rounded font-bold text-sm transition-colors whitespace-nowrap">
                    <i className="ri-calendar-check-line"></i>
                    <span>Schedule Your Appointment</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEGAL DISCLAIMER */}
      <section className="py-8 bg-[#FFF8E7] border-t border-[#F5A623]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-sm text-[#7a5c00] font-medium leading-relaxed">
              I AM NOT AN ATTORNEY IN THE STATE OF NEVADA. I AM NOT AUTHORIZED TO GIVE LEGAL ADVICE OR LEGAL REPRESENTATION. I MAY NOT ACCEPT FEES FOR GIVING LEGAL ADVICE OR LEGAL REPRESENTATION.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Need a <span className="text-[#F5A623]">Duplicate Title?</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Don't let a lost or damaged title slow you down. Our licensed team handles everything fast and accurately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['Licensed 3rd Party Provider', 'Same-Day Processing', 'All Title Types', 'Transparent Pricing'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
